import { getOrgAgentsToolsOptions } from '@onefootprint/fetch-dashboard';
import { useQuery } from '@tanstack/react-query';
import type { AnyExtension } from '@tiptap/core';
import { useEffect, useMemo, useRef } from 'react';
import { createSuggestionExtension } from '@/components/mention-suggestion';
import { useBuilder } from '../../store/provider';
import { AgentBlockExtension, InstructionBlockExtension } from '../agent-blocks/agent-block-extension';
import { buildPromptMentionItems, filterPromptMentionItems, type PromptMentionItem } from './prompt-mention-items';
import PromptMentionList from './prompt-mention-list';
import { createReferenceHighlightExtension } from './reference-highlight';
import { buildReferenceIndex } from './reference-index';

// Typing @ in a prompt editor opens a menu of things the agent can reference: prior
// tasks, inputs, outputs, and enabled tools. Selection splices the reference in as plain
// text (`inputs.<name>` / `outputs.<name>` notation, exact names for tasks/tools) which
// flows verbatim to the model at runtime; a companion decoration extension renders any
// known reference — inserted or hand-typed — as a chip.
// Instruction blocks only compile in task, field and artifact prompts. Anywhere else (norms) they would
// look executable and reach the model as raw JSON, so those editors leave them out.
type Options = {
  instructionBlocks?: boolean;
};
const usePromptMentionExtensions = (currentNodeId?: string, {
  instructionBlocks = true
}: Options = {}): AnyExtension[] => {
  const {
    state
  } = useBuilder();
  const {
    data
  } = useQuery(getOrgAgentsToolsOptions());

  // The extensions are constructed once per editor; both read through this ref so the
  // menu and highlighting always reflect current builder state without rebuilding the editor.
  const sourceRef = useRef({
    bundle: state.bundle,
    toolCatalog: data?.tools ?? [],
    currentNodeId
  });
  useEffect(() => {
    sourceRef.current = {
      bundle: state.bundle,
      toolCatalog: data?.tools ?? [],
      currentNodeId
    };
  });

  // biome-ignore lint/plugin: empty deps — extensions must be created once per editor and are read live via a ref
  return useMemo(() => [createSuggestionExtension<PromptMentionItem>({
    name: 'promptMention',
    items: ({
      query
    }) => {
      const {
        bundle,
        toolCatalog,
        currentNodeId: nodeId
      } = sourceRef.current;
      return filterPromptMentionItems(buildPromptMentionItems({
        bundle,
        toolCatalog
      }, nodeId), query);
    },
    command: ({
      editor,
      range,
      item
    }) => {
      editor.chain().focus().insertContentAt(range, [{
        type: 'text',
        text: `${item.insertText} `
      }]).run();
    },
    renderList: ({
      items,
      query: query_0,
      onSelect,
      listRef
    }) => <PromptMentionList ref={listRef} items={items} query={query_0} onSelect={onSelect} />
  }), createReferenceHighlightExtension(() => {
    const {
      bundle: bundle_0,
      toolCatalog: toolCatalog_0
    } = sourceRef.current;
    return buildReferenceIndex({
      bundle: bundle_0,
      toolCatalog: toolCatalog_0
    });
  }), AgentBlockExtension, ...(instructionBlocks ? [InstructionBlockExtension] : [])], []);
};
export default usePromptMentionExtensions;